import { catchError, map, Observable, of } from 'rxjs';
import { ComponentDataSourceService } from '../../component-data-source.service';
import { environment } from '../../../../environment/environment';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';

const PLATFORM_OPTIONS = [
  { value: 'Windows', label: 'Windows' },
  { value: 'macOS', label: 'macOS' },
  { value: 'iOS', label: 'iOS' },
  { value: 'Android', label: 'Android' },
  { value: 'Linux', label: 'Linux' },
  { value: 'iPadOS', label: 'iPadOS' },
  { value: 'Unknown', label: 'Ukendt' },
] as const;

export function parsePlatformFromDeviceInfo(value: string | null | undefined): string {
  const text = normalizeDeviceInfoText(value);
  if (!text) {
    return 'Unknown';
  }

  if (text.includes('Windows')) {
    return 'Windows';
  }
  if (text.includes('Macintosh') || text.includes('Mac OS')) {
    return 'macOS';
  }
  if (text.includes('iPhone') || text.includes('iOS')) {
    return 'iOS';
  }
  if (text.includes('Android')) {
    return 'Android';
  }
  if (text.includes('Linux')) {
    return 'Linux';
  }
  if (text.includes('iPad')) {
    return 'iPadOS';
  }

  return 'Unknown';
}

function normalizeDeviceInfoText(value: unknown): string {
  if (value == null || value === '') {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function normalizeCprngUsersResponse(response: unknown): Record<string, unknown>[] {
  if (Array.isArray(response)) {
    return response as Record<string, unknown>[];
  }

  if (response && typeof response === 'object') {
    const payload = response as Record<string, unknown>;
    if (Array.isArray(payload['data'])) {
      return payload['data'] as Record<string, unknown>[];
    }
    if (Array.isArray(payload['items'])) {
      return payload['items'] as Record<string, unknown>[];
    }
  }

  return [];
}

function enrichCprngUserRow(row: Record<string, unknown>): Record<string, unknown> {
  const platform =
    (row['platform'] as string | undefined) ??
    parsePlatformFromDeviceInfo(row['device_info'] as string | null | undefined);
  return {
    ...row,
    platform,
  };
}

function getFilterValue(params: LoadDataParams | undefined, column: string): unknown {
  const queryValue = params?.queryParams?.[column];
  if (queryValue != null && queryValue !== '') {
    return queryValue;
  }

  const activeFilters = (params?.filters ?? []).filter(
    (filter) => filter.column === column && filter.value != null && filter.value !== ''
  );
  if (activeFilters.length > 0) {
    return activeFilters[activeFilters.length - 1].value;
  }

  return undefined;
}

function applyClientFilters(
  items: Record<string, unknown>[],
  params?: LoadDataParams
): Record<string, unknown>[] {
  let filtered = items;

  const platformValue = getFilterValue(params, 'platform');
  if (platformValue != null && platformValue !== '') {
    filtered = filtered.filter((row) => row['platform'] === String(platformValue));
  }

  const searchQuery = params?.searchQuery?.trim().toLowerCase();
  if (searchQuery) {
    filtered = filtered.filter((row) => {
      const usersId = String(row['users_id'] ?? '').toLowerCase();
      const fullName = String(row['full_name'] ?? '').toLowerCase();
      const deviceId = String(row['device_identifier'] ?? '').toLowerCase();
      return (
        usersId.includes(searchQuery) ||
        fullName.includes(searchQuery) ||
        deviceId.includes(searchQuery)
      );
    });
  }

  return filtered;
}

type CprngUsersPageResult = {
  items: Record<string, unknown>[];
  totalCount: number;
  pageSize: number;
  pageIndex: number;
};

function getPaginationParams(params?: LoadDataParams): { pageIndex: number; pageSize: number } {
  const pageSizeRaw = getFilterValue(params, 'pageSize');
  const pageIndexRaw = getFilterValue(params, 'pageIndex');
  const pageSize = Math.max(1, Number(pageSizeRaw ?? 10) || 10);
  const pageIndex = Math.max(0, Number(pageIndexRaw ?? 0) || 0);
  return { pageIndex, pageSize };
}

/** Cache key for the full filtered list (search/platform), not pagination or sort. */
function buildFilteredCacheKey(params?: LoadDataParams): string {
  const platform = String(getFilterValue(params, 'platform') ?? '');
  const search = (params?.searchQuery ?? '').trim();
  return `${platform}\0${search}`;
}

function getSortParams(
  params?: LoadDataParams
): { column: string; direction: 'asc' | 'desc' } | null {
  if (params?.sort?.active && params.sort.direction) {
    return { column: params.sort.active, direction: params.sort.direction };
  }

  const column = getFilterValue(params, 'sortColumn');
  const direction = getFilterValue(params, 'sortDirection');
  if (column != null && column !== '' && (direction === 'asc' || direction === 'desc')) {
    return { column: String(column), direction };
  }

  return null;
}

function applyClientSort(
  items: Record<string, unknown>[],
  params?: LoadDataParams
): Record<string, unknown>[] {
  const sort = getSortParams(params);
  if (!sort) {
    return items;
  }

  const direction = sort.direction === 'desc' ? -1 : 1;
  const column = sort.column;

  return [...items].sort((a, b) => {
    const aVal = String(a[column] ?? '');
    const bVal = String(b[column] ?? '');
    return direction * aVal.localeCompare(bVal, undefined, { numeric: true });
  });
}

function toPage(items: Record<string, unknown>[], params?: LoadDataParams): CprngUsersPageResult {
  const sorted = applyClientSort(items, params);
  const totalCount = sorted.length;
  const { pageIndex, pageSize } = getPaginationParams(params);
  const maxPageIndex = totalCount === 0 ? 0 : Math.ceil(totalCount / pageSize) - 1;
  const safePageIndex = Math.min(pageIndex, Math.max(0, maxPageIndex));
  const start = safePageIndex * pageSize;

  return {
    items: sorted.slice(start, start + pageSize),
    totalCount,
    pageSize,
    pageIndex: safePageIndex,
  };
}

export function dunaUsers(componentDataSourceService: ComponentDataSourceService) {
  let cachedFilteredRows: Record<string, unknown>[] | null = null;
  let cachedFilterKey = '';

  return {
    name: 'Duna Users Data Source',
    description: 'Fetches user data for the Duna control panel',
    execute: (params?: LoadDataParams): Observable<CprngUsersPageResult> => {
      const filterKey = buildFilteredCacheKey(params);

      const serveFromCache = (): CprngUsersPageResult => toPage(cachedFilteredRows ?? [], params);

      if (cachedFilteredRows !== null && cachedFilterKey === filterKey) {
        return of(serveFromCache());
      }

      return componentDataSourceService.http.get(`${environment.apiUrl}/cprng/users`).pipe(
        map((response) => {
          const allItems = normalizeCprngUsersResponse(response).map((row) => enrichCprngUserRow(row));
          cachedFilteredRows = applyClientFilters(allItems, params);
          cachedFilterKey = filterKey;
          return serveFromCache();
        }),
        catchError((error: unknown) => {
          console.error('Error fetching users:', error);
          cachedFilteredRows = null;
          cachedFilterKey = '';
          const { pageSize, pageIndex } = getPaginationParams(params);
          return of({ items: [], totalCount: 0, pageSize, pageIndex });
        })
      );
    },
    settings: {
      columns: [
        { key: 'users_id', header: 'User ID', type: 'text' },
        { key: 'full_name', header: 'Fulde navn', type: 'text' },
        {
          key: 'device_identifier',
          header: 'Device ID',
          type: 'text',
          transform: (value: string) => value?.substring(0, 15) + '...',
        },
        {
          key: 'platform',
          header: 'Platform',
          type: 'text',
          transform: (value: string, row?: { device_info?: string | null }) =>
            value ?? parsePlatformFromDeviceInfo(row?.device_info),
        },
        {
          key: 'refresh_token',
          header: 'Refresh Token',
          type: 'text',
          transform: (value: string) => value?.substring(0, 15) + '...',
        },
        {
          key: 'rt_last_used',
          header: 'RT Sidst brugt',
          type: 'text',
          transform: (value: string) => (value ? new Date(value).toLocaleString() : 'N/A'),
        },
        {
          key: 'push_token',
          header: 'Push Token',
          type: 'text',
          transform: (value: string) => value?.substring(0, 15) + '...',
        },
        {
          key: 'pt_last_used',
          header: 'PT Sidst brugt',
          type: 'text',
          transform: (value: string) => (value ? new Date(value).toLocaleString() : 'N/A'),
        },
        {
          key: 'actions',
          header: 'Actions',
          type: 'button',
          buttonText: 'Send Notification',
          buttonColor: 'primary',
          action: (row: any) => {
            componentDataSourceService.pushNotificationService
              .sendPushNotification(row.push_token, 'Test notification', 'This is a test notification')
              .subscribe({
                next: (response) => {
                },
                error: (error) => {
                  console.error('Failed to send push notification:', error);
                },
                complete: () => {
                },
              });
          },
        },
      ],
      pageable: true,
      clientSideFiltering: true,
      sortable: true,
      searchable: true,
      rowHover: true,
      defaultSortColumn: 'full_name',
      defaultSortDirection: 'asc',
      filters: [
        {
          column: 'platform',
          value: '',
          operator: 'equals' as const,
          label: 'Platform',
          options: [...PLATFORM_OPTIONS],
        },
      ],
    },
  };
}
